import { useEffect, useMemo, useState } from "react";
import OutsideClickHandler from "react-outside-click-handler";
import { MdClose } from "react-icons/md";
import { HiOutlineEye } from "react-icons/hi";
import moment from "moment";
import { toast } from "react-toastify";
import { Oval } from "react-loader-spinner";
import { handleError } from "src/Common/function";
import { formatDate } from "src/Common/common";
import AccountService from "src/Services/API/account.service";
import BranchService from "src/Services/API/branch.service";
import UserService from "src/Services/API/user.service";
import ReportService from "src/Services/API/report.service";
import { REPORT_PREVIEW_PAGE_SIZE } from "src/Common/contants";
import DateRangeSelector, { DATE_RANGE_OPTIONS } from "../components/DateRangeSelector";
import PreviewPagination from "../components/PreviewPagination";
import MultiSelectChipInput from "../components/MultiSelectChipInput";
import RecipientsChipInput from "../components/RecipientsChipInput";
import ReportGeneratedModal from "../components/ReportGeneratedModal";

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// Resolve the date-range UI selection into the flags the refrigerant endpoints expect.
const resolveDateFlags = (dateRange, customStart, customEnd) => {
  if (dateRange === "past7") {
    return { past7Days: true, past30Days: false, customDays: false, startDate: null, endDate: null };
  }
  if (dateRange === "past30") {
    return { past7Days: false, past30Days: true, customDays: false, startDate: null, endDate: null };
  }
  if (dateRange === "pastQuarter") {
    // No dedicated flag — express as a custom 90-day range.
    return {
      past7Days: false,
      past30Days: false,
      customDays: true,
      startDate: moment().subtract(90, "days").toDate(),
      endDate: new Date(),
    };
  }
  // custom
  return {
    past7Days: false,
    past30Days: false,
    customDays: true,
    startDate: customStart,
    endDate: customEnd,
  };
};

/**
 * Refrigerant Report modal: filters -> preview -> generate & email.
 *
 * Filters: Customer, Branch, Location, Work Order #, Technician, Date Range.
 * Customer/Branch/Location/Technician are multi-select (empty = "all"); Work
 * Order # is a free-text match.
 *
 * shown / onClose: visibility control
 * userArray: enabled users (for resolving recipient chips)
 */
const RefrigerantReportModal = ({ shown, onClose, userArray = [] }) => {
  const [step, setStep] = useState("filters"); // "filters" | "preview"
  const [customerIds, setCustomerIds] = useState([]);
  const [branchIds, setBranchIds] = useState([]);
  const [locationIds, setLocationIds] = useState([]);
  const [technicianIds, setTechnicianIds] = useState([]);
  const [woNumber, setWoNumber] = useState("");
  const [dateRange, setDateRange] = useState("past30");
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [recipients, setRecipients] = useState([]);

  // Filter option lists.
  const [accounts, setAccounts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [preview, setPreview] = useState(null); // { recordsFound, rows, page, pageSize }
  // 0-based index of the preview page on screen. The backend returns one page
  // per request, so paging re-fetches rather than slicing a local array.
  const [page, setPage] = useState(0);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [generated, setGenerated] = useState(null); // { fileUrl, fileName }

  useEffect(() => {
    if (!shown) return;
    setStep("filters");
    setCustomerIds([]);
    setBranchIds([]);
    setLocationIds([]);
    setTechnicianIds([]);
    setWoNumber("");
    setDateRange("past30");
    setCustomStart(null);
    setCustomEnd(null);
    setRecipients([]);
    setPreview(null);
    setPage(0);
    setIsPreviewing(false);
    setIsSending(false);
    setGenerated(null);
  }, [shown]);

  // Load filter options while the modal is open.
  useEffect(() => {
    if (!shown) return;
    // get-all is customer:read-gated; the Customer/Location filters only need
    // the id/name tree, so use the ungated picker endpoint.
    setLoadingAccounts(true);
    AccountService.getForAssign()
      .then((response) => setAccounts(response.data || []))
      .catch((error) => handleError(error))
      .finally(() => setLoadingAccounts(false));

    setLoadingBranches(true);
    BranchService.getAll()
      .then((response) => setBranches(response.data || []))
      .catch((error) => handleError(error))
      .finally(() => setLoadingBranches(false));

    // getAll is user:read-gated; the picker endpoint returns display fields
    // plus branch ids, which is all the Technician filter needs. No branchId
    // param — branch scoping happens client-side against the selection.
    setLoadingUsers(true);
    UserService.getUsersForAssign(null)
      .then((response) => setUsers(response.data || []))
      .catch((error) => handleError(error))
      .finally(() => setLoadingUsers(false));
  }, [shown]);

  const customerOptions = useMemo(
    () =>
      accounts
        .map((account) => ({ id: account.id, name: account.name }))
        .sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [accounts],
  );

  // Locations are scoped to the selected customers; none selected = all.
  const locationOptions = useMemo(() => {
    const customerSet = new Set(customerIds);
    const inScope = customerIds.length ? accounts.filter((account) => customerSet.has(account.id)) : accounts;
    return inScope
      .flatMap((account) => account.customers || [])
      .map((location) => ({
        id: location.id,
        name: location.companyName || "Location",
        subtitle: location.branch?.name,
      }))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [accounts, customerIds]);

  // Prune selected locations that fall out of scope when the customer changes.
  useEffect(() => {
    if (!locationIds.length || loadingAccounts || accounts.length === 0) return;
    const allowed = new Set(locationOptions.map((option) => option.id));
    const next = locationIds.filter((id) => allowed.has(id));
    if (next.length !== locationIds.length) setLocationIds(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationOptions, loadingAccounts]);

  const branchOptions = useMemo(() => branches.map((branch) => ({ id: branch.id, name: branch.name })), [branches]);

  // Technicians scoped to the selected branches; none selected = all.
  const technicianOptions = useMemo(() => {
    const branchSet = new Set(branchIds);
    return users
      .filter((user) => user.isEnabled)
      .filter((user) => branchSet.size === 0 || (user.branches || []).some((branch) => branchSet.has(branch.id)))
      .map((user) => ({ id: user.id, name: user.name, subtitle: user.email }))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [users, branchIds]);

  // Prune selected technicians that fall out of scope when the branch changes.
  useEffect(() => {
    if (!technicianIds.length || loadingUsers || users.length === 0) return;
    const allowed = new Set(technicianOptions.map((option) => option.id));
    const next = technicianIds.filter((id) => allowed.has(id));
    if (next.length !== technicianIds.length) setTechnicianIds(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [technicianOptions, loadingUsers]);

  const selectableUsers = useMemo(() => userArray.filter((user) => user.email), [userArray]);

  const addRecipient = (email) => {
    if (!isValidEmail(email)) {
      toast("Please enter a valid email address");
      return;
    }
    if (recipients.some((recipient) => recipient.email?.toLowerCase() === email.toLowerCase())) return;
    const matchedUser = selectableUsers.find((user) => user.email?.toLowerCase() === email.toLowerCase());
    setRecipients((prev) => [
      ...prev,
      matchedUser
        ? { id: matchedUser.id, name: matchedUser.name, email: matchedUser.email, avatar: matchedUser.avatar }
        : { email },
    ]);
  };

  const toggleRecipient = (user) => {
    setRecipients((prev) => {
      const exists = prev.some((item) => (item.id ?? item.email) === (user.id ?? user.email));
      if (exists) return prev.filter((item) => (item.id ?? item.email) !== (user.id ?? user.email));
      return [...prev, { id: user.id, name: user.name, email: user.email, avatar: user.avatar }];
    });
  };

  const selectAllRecipients = (checked) => {
    setRecipients(
      checked
        ? selectableUsers.map((user) => ({ id: user.id, name: user.name, email: user.email, avatar: user.avatar }))
        : [],
    );
  };

  const removeRecipient = (recipient) => {
    setRecipients((prev) => prev.filter((item) => (item.id ?? item.email) !== (recipient.id ?? recipient.email)));
  };

  // Validate filters and resolve the API arguments shared by preview + send.
  const buildRequestArgs = () => {
    const flags = resolveDateFlags(dateRange, customStart, customEnd);
    if (flags.customDays && (!flags.startDate || !flags.endDate)) {
      toast("Please choose a start and end date");
      return null;
    }
    return {
      past7Days: flags.past7Days,
      past30Days: flags.past30Days,
      customDays: flags.customDays,
      customStartDate: formatDate(flags.startDate, "MM/DD/YYYY"),
      customEndDate: formatDate(flags.endDate, "MM/DD/YYYY"),
      woNumber: woNumber.trim(),
      filters: { customerIds, branchIds, locationIds, technicianIds },
    };
  };

  // Fetch one page of preview rows. `nextPage` is 0-based; the Preview button
  // starts at 0 and the pager passes the page the user clicked.
  const loadPreview = (nextPage = 0) => {
    // The pager stays clickable while a page loads, so ignore clicks that land
    // mid-flight rather than firing a duplicate for the same page.
    if (isPreviewing) return;
    const args = buildRequestArgs();
    if (!args) return;
    setIsPreviewing(true);
    ReportService.getRefrigerantReportPreview({ ...args, page: nextPage })
      .then((response) => {
        setPreview(response.data || { recordsFound: 0, rows: [] });
        // Only advance once the matching rows are in, so the pager never
        // highlights a page whose rows failed to load.
        setPage(nextPage);
        setStep("preview");
      })
      .catch((error) => handleError(error))
      .finally(() => setIsPreviewing(false));
  };

  const sendReport = () => {
    const recipientIds = recipients
      .filter((recipient) => recipient.id != null)
      .map((recipient) => String(recipient.id));
    if (recipientIds.length === 0) {
      toast("Please select at least one recipient that is a user in this branch");
      return;
    }
    const args = buildRequestArgs();
    if (!args) return;
    setIsSending(true);
    ReportService.sendRefrigerantReport({ ...args, recipients: recipientIds })
      .then((response) => {
        toast("Your report has been sent successfully");
        const data = response?.data || {};
        setGenerated({ fileUrl: data.fileUrl, fileName: data.fileName, fileSize: data.fileSize });
      })
      .catch((error) => handleError(error))
      .finally(() => setIsSending(false));
  };

  const handleSuccessClose = () => {
    setGenerated(null);
    onClose();
  };

  if (!shown) return null;

  if (generated) {
    return (
      <ReportGeneratedModal
        shown
        onClose={handleSuccessClose}
        fileUrl={generated.fileUrl}
        fileName={generated.fileName || "Refrigerant_Usage_Report.xlsx"}
        fileSize={generated.fileSize}
      />
    );
  }

  // Display label for the selected date range (used in preview summary).
  const dateRangeLabel = (() => {
    if (dateRange === "custom") {
      return customStart && customEnd
        ? `${moment(customStart).format("MMM D")} - ${moment(customEnd).format("MMM D, YYYY")}`
        : "Custom Range";
    }
    const option = DATE_RANGE_OPTIONS.find((item) => item.key === dateRange);
    const end = moment();
    const start = moment().subtract(option?.days || 30, "days");
    return `${option?.label} (${start.format("MMM D")} - ${end.format("MMM D")})`;
  })();

  const filterSummary = (selectedIds, allLabel, singular) =>
    selectedIds.length === 0 ? allLabel : `${selectedIds.length} ${singular}${selectedIds.length === 1 ? "" : "s"}`;

  const previewRows = preview?.rows ?? [];
  // recordsFound is the full match count, not this page's length, so it drives
  // the page count.
  const recordsFound = preview?.recordsFound ?? 0;
  const pageSize = preview?.pageSize || REPORT_PREVIEW_PAGE_SIZE;
  const totalPages = Math.max(1, Math.ceil(recordsFound / pageSize));

  return (
    <div className="fixed w-screen h-screen left-0 top-0 bg-black bg-opacity-30 flex items-center justify-center z-30">
      <OutsideClickHandler
        onOutsideClick={(event) => {
          // The filter/recipients dropdowns are portaled to document.body, so
          // clicks inside them count as "outside" this modal. Ignore those.
          if (event.target.closest?.("[data-report-dropdown],[data-recipients-dropdown]")) return;
          onClose();
        }}
      >
        <div
          className={`${
            step === "preview" ? "w-[1000px]" : "w-[760px]"
          } max-h-[90vh] overflow-auto bg-white rounded-[12px] shadow-[0px_10px_10px_-5px_rgba(0,0,0,0.04),0px_20px_25px_-5px_rgba(0,0,0,0.10)]`}
        >
          {/* Header */}
          <div
            className={`flex items-start gap-3 border-b-[1px] border-[#E5E8F3] ${
              step === "preview" ? "px-8 py-6" : "px-6 py-4"
            }`}
          >
            <div className="flex-1 flex flex-col gap-1">
              <p
                className={`font-OpenSansSemiBold capitalize text-[#1A2332] ${
                  step === "preview" ? "text-[24px] leading-[32px]" : "text-[20px] leading-[28px]"
                }`}
              >
                {step === "filters" ? "Generate Refrigerant Report" : "Refrigerant Report Preview"}
              </p>
              <p
                className={`font-OpenSansRegular tracking-[0.14px] text-[#7C829A] ${
                  step === "preview" ? "text-[16px] leading-[24px]" : "text-[14px] leading-[20px]"
                }`}
              >
                {step === "filters"
                  ? "Configure filters and export refrigerant usage data."
                  : "Review the report before sending it to recipients."}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center text-[#868DA6] hover:text-[#1A2332]"
            >
              <MdClose size={18} />
            </button>
          </div>

          {step === "filters" ? (
            <>
              {/* Filters body */}
              <div className="p-8 flex flex-col gap-8">
                {/* Customer / Branch / Location / Work Order # in a 2-column grid. */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                  <MultiSelectChipInput
                    label="Customer"
                    options={customerOptions}
                    selectedIds={customerIds}
                    onChange={setCustomerIds}
                    placeholder="Search customers..."
                    allLabel="All Customers"
                    loading={loadingAccounts}
                  />
                  <MultiSelectChipInput
                    label="Branch"
                    options={branchOptions}
                    selectedIds={branchIds}
                    onChange={setBranchIds}
                    placeholder="Search branches..."
                    allLabel="All Branches"
                    loading={loadingBranches}
                  />
                  <MultiSelectChipInput
                    label="Location"
                    options={locationOptions}
                    selectedIds={locationIds}
                    onChange={setLocationIds}
                    placeholder="Search locations..."
                    allLabel="All Locations"
                    loading={loadingAccounts}
                  />
                  <div className="w-full flex flex-col gap-2">
                    <span className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332] capitalize">
                      Work Order #
                    </span>
                    <input
                      value={woNumber}
                      onChange={(event) => setWoNumber(event.target.value)}
                      placeholder="8834"
                      className="w-full h-10 bg-white rounded-[8px] ring-1 ring-inset ring-[#E5E8F3] focus:ring-[#E5E8F3] outline-none px-3 font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332] placeholder:text-[#868DA6]"
                    />
                  </div>
                </div>

                <MultiSelectChipInput
                  label="Technician"
                  options={technicianOptions}
                  selectedIds={technicianIds}
                  onChange={setTechnicianIds}
                  placeholder="Search technicians..."
                  allLabel="All Technicians"
                  loading={loadingUsers}
                />

                <div className="flex flex-col gap-3">
                  <p className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize text-[#1A2332]">
                    Date Range
                  </p>
                  <DateRangeSelector
                    selected={dateRange}
                    onSelect={setDateRange}
                    customStart={customStart}
                    customEnd={customEnd}
                    onCustomChange={([start, end]) => {
                      setCustomStart(start);
                      setCustomEnd(end);
                    }}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <RecipientsChipInput
                    recipients={recipients}
                    users={selectableUsers}
                    onAdd={addRecipient}
                    onToggle={toggleRecipient}
                    onSelectAll={selectAllRecipients}
                    onRemove={removeRecipient}
                  />
                </div>
              </div>

              {/* Filters footer */}
              <div className="flex items-center justify-end gap-4 px-6 py-4 border-t-[1px] border-[#E5E8F3]">
                <button
                  type="button"
                  onClick={onClose}
                  className="h-10 px-1 rounded-[6px] font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-[#7C829A]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => loadPreview(0)}
                  disabled={isPreviewing}
                  className={`h-10 px-4 rounded-[8px] bg-white outline outline-1 -outline-offset-1 outline-[#C9CEE2] shadow-[0px_1px_2px_rgba(16,24,40,0.05)] flex items-center justify-center gap-2 min-w-[120px] font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-[#1A2332] ${
                    isPreviewing ? "cursor-not-allowed opacity-80" : "hover:bg-[#F7F9FC]"
                  }`}
                >
                  {isPreviewing ? (
                    <Oval visible height="18" width="18" color="#0065FD" strokeWidth={8} secondaryColor="#E5E8F3" />
                  ) : (
                    <>
                      <HiOutlineEye size={16} />
                      Preview
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={sendReport}
                  disabled={isSending}
                  className={`h-10 px-4 rounded-[8px] bg-[#0065FD] flex items-center justify-center min-w-[130px] font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-white ${
                    isSending ? "cursor-not-allowed opacity-80" : "hover:bg-[#0058E5]"
                  }`}
                >
                  {isSending ? (
                    <Oval visible height="18" width="18" color="#FFFFFF" strokeWidth={8} secondaryColor="#FFEEFF" />
                  ) : (
                    "Send Report"
                  )}
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Preview body */}
              <div className="p-8 flex flex-col gap-8">
                <div className="rounded-[10px] bg-[#F8F9FE] outline outline-1 -outline-offset-1 outline-[#E5E8F3] p-6 flex flex-col gap-4">
                  <p className="font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-[#101828]">
                    Report Summary
                  </p>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                    <SummaryField label="Customer" value={filterSummary(customerIds, "All Customers", "Customer")} />
                    <SummaryField label="Branch" value={filterSummary(branchIds, "All Branches", "Branch")} />
                    <SummaryField label="Location" value={filterSummary(locationIds, "All Locations", "Location")} />
                    <SummaryField label="Work Order #" value={woNumber.trim() || "All Work Orders"} />
                    <SummaryField
                      label="Technician"
                      value={filterSummary(technicianIds, "All Technicians", "Technician")}
                    />
                    <SummaryField label="Date Range" value={dateRangeLabel} />
                    <SummaryField label="Records Found" value={String(preview?.recordsFound ?? 0)} />
                  </div>
                  <div className="border-t-[1px] border-[#E5E8F3] pt-4 flex flex-col gap-2">
                    <p className="font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] uppercase text-[#7C829A]">
                      Recipients
                    </p>
                    <div className="flex flex-wrap content-center items-center gap-2">
                      {recipients.length === 0 ? (
                        <span className="font-OpenSansRegular text-[14px] text-[#868DA6]">No recipients selected</span>
                      ) : (
                        recipients.map((recipient, index) => (
                          <span
                            key={recipient.id ?? recipient.email ?? index}
                            className="flex items-center gap-1 pl-3 pr-2.5 py-1 rounded-[16px] bg-[#FBFCFF] outline outline-1 -outline-offset-1 outline-[#E5E8F3]"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="12"
                              height="12"
                              viewBox="0 0 12 12"
                              fill="none"
                            >
                              <path
                                d="M10.7983 3.18359C10.973 3.07235 11.2051 3.12373 11.3164 3.29834C11.4277 3.47299 11.3763 3.70512 11.2017 3.81641L6.70605 6.67969C6.70174 6.68244 6.6973 6.68542 6.69287 6.68799C6.48318 6.80976 6.24492 6.8735 6.00244 6.87354C5.75987 6.87354 5.52128 6.80982 5.31152 6.68799C5.30715 6.68545 5.30309 6.6824 5.29883 6.67969L0.798828 3.81641C0.624138 3.70524 0.572507 3.47354 0.683594 3.29883C0.794755 3.12414 1.02646 3.07251 1.20117 3.18359L5.68799 6.03906C5.78333 6.09444 5.89218 6.12354 6.00244 6.12354C6.1126 6.1235 6.22065 6.09437 6.31592 6.03906L10.7983 3.18359Z"
                                fill="#1A2332"
                              />
                              <path
                                d="M10.625 3C10.625 2.65482 10.3452 2.375 10 2.375H2C1.65482 2.375 1.375 2.65482 1.375 3V9C1.375 9.34518 1.65482 9.625 2 9.625H10C10.3452 9.625 10.625 9.34518 10.625 9V3ZM11.375 9C11.375 9.75939 10.7594 10.375 10 10.375H2C1.24061 10.375 0.625 9.75939 0.625 9V3C0.625 2.24061 1.24061 1.625 2 1.625H10C10.7594 1.625 11.375 2.24061 11.375 3V9Z"
                                fill="#1A2332"
                              />
                            </svg>
                            <span className="text-center font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332]">
                              {recipient.name && recipient.email
                                ? `${recipient.name} (${recipient.email})`
                                : recipient.email || recipient.name}
                            </span>
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  {/* Records table, with the pager as its footer. */}
                  <div className="rounded-[12px] bg-white shadow-[0px_1px_2px_rgba(0,0,0,0.06)] outline outline-1 -outline-offset-1 outline-[#E5E8F3] overflow-hidden">
                    {previewRows.length ? (
                      <>
                      {/* Paging swaps the rows in place, so dim them while the
                          next page is in flight — but not the pager itself. */}
                      <div className={`flex items-start ${isPreviewing ? "opacity-60" : ""}`}>
                        <PreviewColumn
                          header="Date"
                          rows={previewRows}
                          className="flex-1"
                          render={(row) => (
                            <span className="font-OpenSansSemiBold text-[14px] leading-[20px] text-[#1A2332]">
                              {row.date}
                            </span>
                          )}
                        />
                        <PreviewColumn
                          header="WO #"
                          rows={previewRows}
                          className="w-[200px]"
                          render={(row) => (
                            <span className="font-OpenSansRegular text-[14px] leading-[20px] text-[#5F6477]">
                              {row.woNumber}
                            </span>
                          )}
                        />
                        <PreviewColumn
                          header="Customer"
                          rows={previewRows}
                          className="w-[200px]"
                          render={(row) => (
                            <span className="font-OpenSansRegular text-[14px] leading-[20px] text-[#5F6477] truncate">
                              {row.customer}
                            </span>
                          )}
                        />
                        <PreviewColumn
                          header="Refrigerant Type"
                          rows={previewRows}
                          className="w-[200px]"
                          render={(row) => (
                            <span className="font-OpenSansRegular text-[14px] leading-[20px] text-[#5F6477]">
                              {row.refrigerantType}
                            </span>
                          )}
                        />
                        <PreviewColumn
                          header="Amount"
                          rows={previewRows}
                          className="w-[200px]"
                          render={(row) => (
                            <span className="font-OpenSansRegular text-[14px] leading-[20px] text-[#5F6477]">
                              {row.amount}
                            </span>
                          )}
                        />
                      </div>
                      <PreviewPagination
                        currentPage={page + 1}
                        totalPages={totalPages}
                        totalItems={recordsFound}
                        pageSize={pageSize}
                        onPageChange={(nextPage) => loadPreview(nextPage - 1)}
                      />
                      </>
                    ) : (
                      <div className="px-5 py-8 text-center font-OpenSansRegular text-[14px] text-[#868DA6]">
                        No refrigerant usage found for the selected filters.
                      </div>
                    )}
                  </div>
                  <p className="font-OpenSansRegular italic text-[14px] leading-[20px] text-[#7C829A]">
                    Preview only. The full report is generated when you send it.
                  </p>
                </div>
              </div>

              {/* Preview footer */}
              <div className="flex items-center justify-end gap-4 px-6 py-4 border-t-[1px] border-[#E5E8F3]">
                <button
                  type="button"
                  onClick={() => setStep("filters")}
                  className="h-10 px-4 rounded-[8px] bg-white outline outline-1 -outline-offset-1 outline-[#C9CEE2] shadow-[0px_1px_2px_rgba(16,24,40,0.05)] font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-[#1A2332] hover:bg-[#F7F9FC]"
                >
                  Back To Filters
                </button>
                <button
                  type="button"
                  onClick={sendReport}
                  disabled={isSending}
                  className={`h-10 px-4 rounded-[8px] bg-[#0065FD] flex items-center justify-center min-w-[130px] font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-white ${
                    isSending ? "cursor-not-allowed opacity-80" : "hover:bg-[#0058E5]"
                  }`}
                >
                  {isSending ? (
                    <Oval visible height="18" width="18" color="#FFFFFF" strokeWidth={8} secondaryColor="#FFEEFF" />
                  ) : (
                    "Send Report"
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </OutsideClickHandler>
    </div>
  );
};

const SummaryField = ({ label, value }) => (
  <div className="flex flex-col gap-1 min-w-0">
    <p className="font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] uppercase text-[#7C829A]">
      {label}
    </p>
    <p className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize text-[#1A2332]">
      {value}
    </p>
  </div>
);

// A single column of the preview table: a sticky-looking header cell followed by
// one cell per row. Rendering column-by-column (rather than row-by-row) keeps each
// column a fixed width and lets long Customer values truncate independently.
const PreviewColumn = ({ header, rows, render, className = "" }) => (
  <div className={`flex flex-col ${className}`}>
    <div className="h-11 px-4 py-3 bg-[#F8F9FE] border-b-[1px] border-[#E5E8F3] flex items-center">
      <span className="font-OpenSansSemiBold text-[12px] leading-[18px] text-[#5F6477]">{header}</span>
    </div>
    {rows.map((row, index) => (
      <div
        key={index}
        className="px-4 py-3 border-b-[1px] border-[#E5E8F3] last:border-b-0 flex items-center overflow-hidden"
      >
        {render(row)}
      </div>
    ))}
  </div>
);

export default RefrigerantReportModal;
